import { EmailTagsInput } from "@storytell/ui/email-tags-input";
import { TbLoader2 } from "solid-icons/tb";
import { createSignal, Match, Show, Switch } from "solid-js";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { extractErrorInfo } from "../util/errorInfo";
import { dateOnly, localToRfc3339, rfc3339ToLocal } from "./calendarRenderUtils";
import {
  type CalendarAction,
  type ConfirmActionPayload,
  isActionConfigAction,
  isSlackTextAction,
  type SendEmailAction,
  type SlackTextAction,
} from "./confirmActionSchema";

interface ConfirmActionEditProps {
  payload: ConfirmActionPayload;
  notify: boolean;
  onNotifyChange: (next: boolean) => void;
  isSubmitting: boolean;
  error: unknown;
  onApprove: (editedParams: Record<string, unknown>) => void;
  onCancel: () => void;
}

export const ConfirmActionEdit = (props: ConfirmActionEditProps) => {
  const rt = useChatRuntime();
  const t = rt.t;

  const isEmail = () => props.payload.actionKind === "send_email";
  // Slack reactions are never editable, so only text-carrying kinds reach here.
  const isSlack = () => isSlackTextAction(props.payload);
  const actionPayload = () => (isActionConfigAction(props.payload) ? props.payload : undefined);
  const isCalendar = () => !isEmail() && !isSlack() && !actionPayload();
  const emailPreview = () => (props.payload as SendEmailAction).preview;
  const calPreview = () => (props.payload as CalendarAction).preview;
  const slackPreview = () => (props.payload as SlackTextAction).preview;

  const [slackText, setSlackText] = createSignal(isSlack() ? slackPreview().text : "");

  const [to, setTo] = createSignal<string[]>(isEmail() ? [...emailPreview().to] : []);
  const [cc, setCc] = createSignal<string[]>(isEmail() ? [...(emailPreview().cc ?? [])] : []);
  const [subject, setSubject] = createSignal(isEmail() ? emailPreview().subject : "");
  const [body, setBody] = createSignal(isEmail() ? emailPreview().body : "");

  const [title, setTitle] = createSignal(isCalendar() ? calPreview().title : "");
  const [allDay, setAllDay] = createSignal(isCalendar() && calPreview().allDay === true);
  const initialStart = () => calPreview().start ?? "";
  const initialEnd = () => calPreview().end ?? "";
  const [start, setStart] = createSignal(
    !isCalendar() ? "" : allDay() ? dateOnly(initialStart()) : rfc3339ToLocal(initialStart())
  );
  const [end, setEnd] = createSignal(
    !isCalendar() ? "" : allDay() ? dateOnly(initialEnd()) : rfc3339ToLocal(initialEnd())
  );
  const [attendees, setAttendees] = createSignal<string[]>(isCalendar() ? [...(calPreview().attendees ?? [])] : []);
  const [location, setLocation] = createSignal(isCalendar() ? (calPreview().location ?? "") : "");
  const [description, setDescription] = createSignal(isCalendar() ? (calPreview().description ?? "") : "");
  const [addMeet, setAddMeet] = createSignal(isCalendar() && calPreview().addMeet === true);
  const [calendarId, setCalendarId] = createSignal(isCalendar() ? (calPreview().calendarId ?? "") : "");

  // Recurrence has no editor here, but the server overwrites it from the confirmed
  // params, so carry the proposal's value through to avoid blanking an existing series.
  const recurrence = () => (isCalendar() ? (calPreview().recurrence ?? []) : []);

  // All-day and timed pickers read their values differently (date-only vs local
  // wall-clock), so translate the fields on toggle to preserve the day.
  const toggleAllDay = (next: boolean) => {
    const s = start();
    const e = end();
    if (next) {
      setStart(dateOnly(s));
      setEnd(dateOnly(e));
    } else {
      setStart(s && !s.includes("T") ? `${s}T09:00` : s);
      setEnd(e && !e.includes("T") ? `${e}T10:00` : e);
    }
    setAllDay(next);
  };

  const buildEmailParams = (): Record<string, unknown> => {
    const params: Record<string, unknown> = {
      to: to(),
      subject: subject(),
      body: body(),
    };
    if (cc().length > 0) params.cc = cc();
    return params;
  };

  // Keep the original instant when the picker value is unchanged: the picker has
  // no seconds, so re-serializing would drop a stored event's sub-minute
  // precision and shift it by up to 59s on an edit that left it alone.
  const serializeTime = (value: string, original: string): string => {
    if (allDay()) return dateOnly(value);
    if (original && rfc3339ToLocal(original) === value) return original;
    return localToRfc3339(value);
  };

  const buildCalendarParams = (): Record<string, unknown> => {
    const params: Record<string, unknown> = {
      title: title(),
      allDay: allDay(),
      attendees: attendees(),
      addMeet: addMeet(),
      recurrence: recurrence(),
    };
    if (start()) params.start = serializeTime(start(), initialStart());
    if (end()) params.end = serializeTime(end(), initialEnd());
    if (location()) params.location = location();
    if (description()) params.description = description();
    if (calendarId()) params.calendarId = calendarId();
    return params;
  };

  const submit = () => {
    if (isSlack()) {
      props.onApprove({ text: slackText() });
      return;
    }
    props.onApprove(isEmail() ? buildEmailParams() : buildCalendarParams());
  };

  const errorMessage = (): string => {
    const info = extractErrorInfo(props.error);
    if (typeof info.code === "string") {
      const translated = t(info.code);
      if (translated && translated !== info.code) return translated;
    }
    return info.message ?? t("confirm_action_card.failed");
  };

  const fieldLabelClass = "block text-xs font-medium text-secondary-text";
  const inputClass =
    "mt-1 w-full rounded-md border border-primary-border bg-primary-bg px-3 py-2 text-sm text-primary-text outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50";

  // The action variant owns its own signals, validation, and submit row, so it
  // replaces this layout rather than adding a Match arm.
  const editForm = () => (
    <div class="@container mt-3 space-y-3">
      <Switch>
        <Match when={isEmail()}>
          <EmailTagsInput
            emails={to}
            onEmailsChange={setTo}
            label={t("confirm_action_card.field_to")}
            disabled={props.isSubmitting}
          />
          <EmailTagsInput
            emails={cc}
            onEmailsChange={setCc}
            label={t("confirm_action_card.field_cc")}
            disabled={props.isSubmitting}
          />
          <div>
            <label class={fieldLabelClass} for="confirm-edit-subject">
              {t("confirm_action_card.field_subject")}
            </label>
            <input
              id="confirm-edit-subject"
              type="text"
              class={inputClass}
              value={subject()}
              disabled={props.isSubmitting}
              onInput={(e) => setSubject(e.currentTarget.value)}
            />
          </div>
          <div>
            <label class={fieldLabelClass} for="confirm-edit-body">
              {t("confirm_action_card.field_body")}
            </label>
            <textarea
              id="confirm-edit-body"
              rows={8}
              class={`${inputClass} whitespace-pre-wrap`}
              value={body()}
              disabled={props.isSubmitting}
              onInput={(e) => setBody(e.currentTarget.value)}
            />
          </div>
        </Match>

        <Match when={isSlack()}>
          <div>
            <label class={fieldLabelClass} for="confirm-edit-slack-text">
              {t("confirm_action_card.field_message")}
            </label>
            <textarea
              id="confirm-edit-slack-text"
              rows={6}
              class={`${inputClass} whitespace-pre-wrap`}
              value={slackText()}
              disabled={props.isSubmitting}
              onInput={(e) => setSlackText(e.currentTarget.value)}
            />
          </div>
        </Match>

        <Match when={isCalendar()}>
          <div>
            <label class={fieldLabelClass} for="confirm-edit-title">
              {t("confirm_action_card.field_title")}
            </label>
            <input
              id="confirm-edit-title"
              type="text"
              class={inputClass}
              value={title()}
              disabled={props.isSubmitting}
              onInput={(e) => setTitle(e.currentTarget.value)}
            />
          </div>
          <label class="flex cursor-pointer items-center gap-2 text-xs text-secondary-text">
            <input
              type="checkbox"
              class="size-4 rounded border-primary-border text-brand focus-visible:ring-2 focus-visible:ring-brand"
              checked={allDay()}
              disabled={props.isSubmitting}
              onChange={(e) => toggleAllDay(e.currentTarget.checked)}
            />
            {t("confirm_action_card.field_all_day")}
          </label>
          <div class="grid grid-cols-1 gap-3 @sm:grid-cols-2">
            <div>
              <label class={fieldLabelClass} for="confirm-edit-start">
                {t("confirm_action_card.field_start")}
              </label>
              <input
                id="confirm-edit-start"
                type={allDay() ? "date" : "datetime-local"}
                class={inputClass}
                value={start()}
                disabled={props.isSubmitting}
                onInput={(e) => setStart(e.currentTarget.value)}
              />
            </div>
            <div>
              <label class={fieldLabelClass} for="confirm-edit-end">
                {t("confirm_action_card.field_end")}
              </label>
              <input
                id="confirm-edit-end"
                type={allDay() ? "date" : "datetime-local"}
                class={inputClass}
                value={end()}
                disabled={props.isSubmitting}
                onInput={(e) => setEnd(e.currentTarget.value)}
              />
            </div>
          </div>
          <EmailTagsInput
            emails={attendees}
            onEmailsChange={setAttendees}
            label={t("confirm_action_card.field_attendees")}
            disabled={props.isSubmitting}
          />
          <div>
            <label class={fieldLabelClass} for="confirm-edit-location">
              {t("confirm_action_card.field_location")}
            </label>
            <input
              id="confirm-edit-location"
              type="text"
              class={inputClass}
              value={location()}
              disabled={props.isSubmitting}
              onInput={(e) => setLocation(e.currentTarget.value)}
            />
          </div>
          <div>
            <label class={fieldLabelClass} for="confirm-edit-description">
              {t("confirm_action_card.field_description")}
            </label>
            <textarea
              id="confirm-edit-description"
              rows={4}
              class={`${inputClass} whitespace-pre-wrap`}
              value={description()}
              disabled={props.isSubmitting}
              onInput={(e) => setDescription(e.currentTarget.value)}
            />
          </div>
          <div>
            <label class={fieldLabelClass} for="confirm-edit-calendar">
              {t("confirm_action_card.field_calendar")}
            </label>
            <input
              id="confirm-edit-calendar"
              type="text"
              class={inputClass}
              value={calendarId()}
              placeholder={t("confirm_action_card.calendar_primary")}
              disabled={props.isSubmitting}
              onInput={(e) => setCalendarId(e.currentTarget.value)}
            />
          </div>
          <label class="flex cursor-pointer items-center gap-2 text-xs text-secondary-text">
            <input
              type="checkbox"
              class="size-4 rounded border-primary-border text-brand focus-visible:ring-2 focus-visible:ring-brand"
              checked={addMeet()}
              disabled={props.isSubmitting}
              onChange={(e) => setAddMeet(e.currentTarget.checked)}
            />
            {t("confirm_action_card.field_add_meet")}
          </label>
          <label class="flex cursor-pointer items-center gap-2 text-xs text-secondary-text">
            <input
              type="checkbox"
              class="size-4 rounded border-primary-border text-brand focus-visible:ring-2 focus-visible:ring-brand"
              checked={props.notify}
              disabled={props.isSubmitting}
              onChange={(e) => props.onNotifyChange(e.currentTarget.checked)}
            />
            {t("confirm_action_card.notify_attendees")}
          </label>
        </Match>
      </Switch>

      <Show when={props.error}>
        <p class="text-sm text-red-500">{errorMessage()}</p>
      </Show>

      <div class="flex flex-col gap-2 @sm:flex-row">
        <button
          type="button"
          onClick={submit}
          disabled={props.isSubmitting}
          aria-busy={props.isSubmitting}
          class="flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 @sm:w-auto"
        >
          <Show when={props.isSubmitting}>
            <TbLoader2 class="size-4 animate-spin" />
          </Show>
          <span>{props.isSubmitting ? t("confirm_action_card.sending") : t("confirm_action_card.approve")}</span>
        </button>
        <button
          type="button"
          onClick={() => props.onCancel()}
          disabled={props.isSubmitting}
          class="flex items-center justify-center gap-2 rounded-md border border-primary-border px-4 py-2 text-sm font-medium text-primary-text transition-colors hover:bg-panel-hover focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 @sm:w-auto"
        >
          {t("confirm_action_card.cancel")}
        </button>
      </div>
    </div>
  );

  return (
    <Show when={actionPayload()} fallback={editForm()}>
      {(p) =>
        /* The rich action editor is host-supplied: it leans on bluesky's
           schedule pickers, so hosts without the slot (chief) fall back to the
           generic field editor. */
        rt.slots?.confirmActionEditor?.({
          payload: p(),
          isSubmitting: props.isSubmitting,
          error: props.error,
          onApprove: props.onApprove,
          onCancel: props.onCancel,
        }) ?? editForm()
      }
    </Show>
  );
};
